// src/pages/employer/EmployerJobLibrary.jsx
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { orgAPI, snippetAPI } from '../../services/api';
import { useConfirm, EditBtn, AddBtn, DelBtn, usePersistedState } from '../../components/shared/UI';
import { SectionModal, LANGUAGES } from '../../components/shared/FieldModals';
import { RichTextModal } from '../../components/shared/RichTextModal';
import { Briefcase, ChevronDown, ChevronUp, X, Upload } from 'lucide-react';

const PARSE_ACCEPT = '.pdf,.doc,.docx,.odt,.rtf';

// ── Constants ──────────────────────────────────────────────

const ADMIN_LEVELS = [
  { value: 'UNSPECIFIED',       label: 'Unspecified'       },
  { value: 'VOLUNTEER',         label: 'Volunteer'         },
  { value: 'OFFICER',           label: 'Officer'           },
  { value: 'SUPERVISOR',        label: 'Supervisor'        },
  { value: 'ASSISTANT_MANAGER', label: 'Assistant Manager' },
  { value: 'MANAGER',           label: 'Manager'           },
  { value: 'SENIOR_MANAGER',    label: 'Senior Manager'    },
  { value: 'EXECUTIVE_MANAGER', label: 'Executive Manager' },
];

const ADMIN_LEVEL_LABEL = Object.fromEntries(ADMIN_LEVELS.map((s) => [s.value, s.label]));

const SCHEDULES = [
  { value: '',           label: 'Not specified' },
  { value: 'FULL_TIME',  label: 'Full Time'     },
  { value: 'PART_TIME',  label: 'Part Time'     },
  { value: 'CASUAL',     label: 'Casual'        },
  { value: 'CONTRACT',   label: 'Contract'      },
];

const SCHEDULE_LABEL = Object.fromEntries(SCHEDULES.filter((s) => s.value).map((s) => [s.value, s.label]));

const REQUIREMENT_OPTIONS = [
  { value: '',          label: 'Unspecified' },
  { value: 'REQUIRED',  label: 'Required'    },
  { value: 'PREFERRED', label: 'Preferred'   },
];

const EDUCATION_LEVELS = [
  { value: '',                        label: '— Not required —'         },
  { value: 'High School Certificate', label: 'High School Certificate'  },
  { value: 'Diploma',                 label: 'Diploma'                  },
  { value: 'Advanced Diploma',        label: 'Advanced Diploma'         },
  { value: 'Graduate Certificate',    label: 'Graduate Certificate'     },
  { value: 'Graduate Diploma',        label: 'Graduate Diploma'         },
  { value: 'Bachelor Degree',         label: 'Bachelor Degree'          },
  { value: 'Master Degree',           label: 'Master Degree'            },
  { value: 'Doctorate',               label: 'Doctorate'                },
];

const EXP_MIN_YEARS = [
  { value: '',   label: 'Any experience' },
  { value: '1',  label: '1+ years'       },
  { value: '2',  label: '2+ years'       },
  { value: '3',  label: '3+ years'       },
  { value: '5',  label: '5+ years'       },
  { value: '7',  label: '7+ years'       },
  { value: '10', label: '10+ years'      },
];

const EXP_YEAR_LABEL = Object.fromEntries(EXP_MIN_YEARS.filter(e => e.value).map(e => [e.value, e.label]));

const ATTRIBUTE_SECTIONS = [
  { title: 'Cognitive Abilities', items: ['Logical reasoning','Abstract thinking','Spatial awareness','Pattern recognition','Memory retention','Processing speed','Concentration/focus','Critical thinking','Creative thinking','Problem solving','Decision making','Mental flexibility/adaptability','Numerical reasoning','Verbal reasoning','Inductive reasoning','Deductive reasoning'] },
  { title: 'Physical Abilities', items: ['Strength','Endurance/stamina','Flexibility','Agility','Balance','Coordination','Reaction time','Manual dexterity','Fine motor control','Gross motor control','Speed','Hand-eye coordination'] },
  { title: 'Sensory Abilities', items: ['Visual acuity','Colour perception','Depth perception','Auditory discrimination','Pitch recognition','Spatial hearing','Tactile sensitivity'] },
  { title: 'Interpersonal & Emotional Abilities', items: ['Empathy','Emotional regulation','Reading social cues','Intuition','Self-awareness','Persuasiveness','Charisma','Patience','Resilience','Stress tolerance','Impulse control'] },
  { title: 'Creative & Perceptual Abilities', items: ['Aesthetic sensitivity','Musical aptitude','Linguistic aptitude','Visual-spatial imagination','Originality/novelty generation'] },
  { title: 'Organisational & Cognitive Style', items: ['Attention to detail','Big picture thinking','Multi-tasking capacity','Systematic thinking','Lateral thinking'] },
];

const PROFICIENCY_LEVELS = [
  { value: '',              label: '— Not specified —' },
  { value: 'native',        label: 'Native'            },
  { value: 'fluent',        label: 'Fluent'            },
  { value: 'advanced',      label: 'Advanced'          },
  { value: 'professional',  label: 'Professional'      },
  { value: 'intermediate',  label: 'Intermediate'      },
  { value: 'conversational',label: 'Conversational'    },
  { value: 'basic',         label: 'Basic'             },
];
const PROFICIENCY_LABEL = Object.fromEntries(PROFICIENCY_LEVELS.filter(l => l.value).map(l => [l.value, l.label]));

const SKILL_SUBCATEGORIES = [
  { key: 'general',  label: 'General Skills'                   },
  { key: 'soft',     label: 'Soft / People Skills'             },
  { key: 'software', label: 'Software & Application Knowledge' },
  { key: 'hardware', label: 'Hardware & Equipment Knowledge'   },
];

const newId = () => crypto.randomUUID();

// ── Shared styles ──────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: 13,
  background: 'white', color: 'var(--text)', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};

const subHeaderStyle = {
  background: '#4A6FA5', color: 'white',
  padding: '7px 14px', display: 'flex', alignItems: 'center',
  justifyContent: 'space-between',
};

const fieldRowStyle   = { display: 'flex', alignItems: 'flex-start', padding: '6px 16px' };
const fieldLabelStyle = {
  fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  minWidth: 110, flexShrink: 0, paddingTop: 2, textAlign: 'right',
};
const fieldValueStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 14px', flex: 1 };
const emptyStyle      = { fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 };

// Sub-section divider — clickable header bar within a card
const subSectionDivStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '7px 14px', background: '#E8F0F8',
  borderTop: '1px solid #C8D8EC', cursor: 'pointer', userSelect: 'none',
};
const subSectionLabelStyle = {
  fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.08em', color: '#4A6FA5',
};

// ICP field heading within the expanded ideal candidate section
const icpFieldLabelStyle = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.06em', color: 'var(--secondary)',
};

// ── RadioGroup ─────────────────────────────────────────────

const RadioGroup = ({ name = 'requirement', value, onChange }) => (
  <div style={{ display: 'flex', gap: 24, marginTop: 4 }}>
    {REQUIREMENT_OPTIONS.map(opt => (
      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          style={{ accentColor: 'var(--secondary)', width: 15, height: 15, cursor: 'pointer' }}
        />
        {opt.label}
      </label>
    ))}
  </div>
);

// ── RoleItem — per-role inline editor ──────────────────────

const RoleItem = ({ role, roleIndex, isAdmin, initialEditing, onSave, onDelete }) => {
  const [editing, setEditing]   = useState(!!initialEditing);
  const [title, setTitle]       = useState(role.title || '');
  const [tasks, setTasks]       = useState(() => (role.tasks || []).map((t) => ({ ...t })));
  const [saving, setSaving]     = useState(false);
  const inputRefs               = useRef({});
  const pendingFocusId          = useRef(null);

  useEffect(() => {
    if (!editing) {
      setTitle(role.title || '');
      setTasks((role.tasks || []).map((t) => ({ ...t })));
    }
  }, [role, editing]);

  useEffect(() => {
    if (pendingFocusId.current && inputRefs.current[pendingFocusId.current]) {
      inputRefs.current[pendingFocusId.current].focus();
      pendingFocusId.current = null;
    }
  });

  const startEdit = () => {
    setTitle(role.title || '');
    setTasks((role.tasks || []).map((t) => ({ ...t })));
    setEditing(true);
  };

  const cancel = () => {
    setTitle(role.title || '');
    setTasks((role.tasks || []).map((t) => ({ ...t })));
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(role.id, { title, tasks: tasks.filter(t => t.text.trim()) });
      setEditing(false);
    } finally { setSaving(false); }
  };

  const addTask = () => {
    if (tasks.length > 0 && !tasks[tasks.length - 1].text.trim()) return;
    const id = newId();
    setTasks((prev) => [...prev, { id, text: '' }]);
    pendingFocusId.current = id;
  };

  const removeTask = (id) => setTasks((prev) => prev.filter((t) => t.id !== id));
  const updateTask = (id, text) => setTasks((prev) => prev.map((t) => t.id === id ? { ...t, text } : t));

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginBottom: 10, overflow: 'hidden' }}>
      <div style={{ background: '#F0F7FF', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
          textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
          Role {roleIndex + 1}
        </span>
        {editing ? (
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ ...inputStyle, flex: 1, background: 'white' }}
            placeholder="e.g. Financial Reporting, Client Management…"
            autoFocus
          />
        ) : (
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 0 }}>
            {role.title || <em style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Untitled role</em>}
          </span>
        )}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {!editing && (
              <EditBtn onClick={startEdit} title="Edit" />
            )}
            <DelBtn onClick={() => onDelete(role.id)} title="Remove" />
          </div>
        )}
      </div>
      <div style={{ padding: '10px 12px' }}>
        {editing ? (
          <>
            {tasks.map((task, ti) => (
              <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, width: 18, textAlign: 'right' }}>
                  {ti + 1}.
                </span>
                <input
                  ref={(el) => { inputRefs.current[task.id] = el; }}
                  value={task.text}
                  onChange={(e) => updateTask(task.id, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Describe a main task…"
                />
                <DelBtn onClick={() => removeTask(task.id)} />
              </div>
            ))}
            <AddBtn onClick={addTask} title="Add Task" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button className="btn btn-ghost btn-sm" onClick={cancel}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !title.trim()}>
                {saving ? 'Saving…' : 'Save Role'}
              </button>
            </div>
          </>
        ) : tasks.filter(t => t.text.trim()).length > 0 ? (
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {tasks.filter(t => t.text.trim()).map((task) => (
              <li key={task.id} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 3, lineHeight: 1.5, listStyle: 'decimal' }}>
                {task.text}
              </li>
            ))}
          </ol>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
            No tasks added.{isAdmin && ' Click Edit to add tasks.'}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Position modal (Occupation, Admin Level, Workplace, Schedule) ──

const PositionModal = ({ initial, workplaces, onSave, onClose }) => {
  const [form, setForm] = useState({
    name:           initial?.name           || '',
    seniorityLevel: initial?.seniorityLevel || 'UNSPECIFIED',
    workplaceId:    initial?.workplaceId    || '',
    schedule:       initial?.schedule       || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        workplaceId: form.workplaceId || null,
        schedule:    form.schedule    || null,
      });
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span>{initial ? 'Edit Position' : 'Add Position'}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 4 }}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row" style={{ marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Occupation <span style={{ color: '#CC0000' }}>*</span></label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={inputStyle} placeholder="e.g. Senior Accountant" autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Admin Level</label>
              <select value={form.seniorityLevel}
                onChange={(e) => setForm((f) => ({ ...f, seniorityLevel: e.target.value }))}
                style={inputStyle}>
                {ADMIN_LEVELS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div>
              <label style={labelStyle}>Workplace</label>
              <select value={form.workplaceId}
                onChange={(e) => setForm((f) => ({ ...f, workplaceId: e.target.value }))}
                style={inputStyle}>
                <option value="">— Not assigned —</option>
                {workplaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Schedule</label>
              <select value={form.schedule}
                onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))}
                style={inputStyle}>
                {SCHEDULES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}
            disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : 'Save Position'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Ideal Candidate editing modals ─────────────────────────

// IcpLanguagesModal — per-language Required/Preferred radio
// Stores [{ id, name, requirement }] (handles legacy string[] on load)
const IcpLanguagesModal = ({ initial, saving, onSave, onCancel }) => {
  const [items, setItems] = useState(() =>
    (initial || []).map(item =>
      typeof item === 'string'
        ? { id: newId(), name: item, requirement: '' }
        : { ...item }
    )
  );
  const [input, setInput]       = useState('');
  const [showList, setShowList] = useState(false);
  const [dropPos, setDropPos]   = useState({ top: 0, left: 0, width: 0 });
  const inputRef                = useRef(null);
  const wrapRef                 = useRef(null);

  const selectedNames = items.map(i => i.name);
  const filtered = LANGUAGES.filter(
    l => l.toLowerCase().includes(input.toLowerCase()) && !selectedNames.includes(l)
  ).slice(0, 8);

  const openDrop = () => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setShowList(true);
  };

  const addLang = (name) => {
    setItems(prev => [...prev, { id: newId(), name, proficiency: '', requirement: '' }]);
    setInput('');
    setShowList(false);
    inputRef.current?.focus();
  };

  const removeLang = (id) => setItems(prev => prev.filter(i => i.id !== id));
  const setReq = (id, req) => setItems(prev => prev.map(i => i.id === id ? { ...i, requirement: req } : i));

  return (
    <SectionModal title="Edit Languages" saving={saving} wide onSave={() => onSave(items)} onCancel={onCancel}>
      <div style={{ marginBottom: 14 }} ref={wrapRef}>
        <label style={labelStyle}>Add Language</label>
        <input
          ref={inputRef}
          type="text" value={input}
          onChange={e => { setInput(e.target.value); openDrop(); }}
          onFocus={openDrop}
          onBlur={() => setTimeout(() => setShowList(false), 150)}
          style={inputStyle}
          placeholder="Type to search languages…"
          autoFocus
        />
        {showList && filtered.length > 0 && ReactDOM.createPortal(
          <div style={{
            position: 'absolute', top: dropPos.top, left: dropPos.left, width: dropPos.width,
            zIndex: 9999, background: 'white', border: '1px solid var(--border)', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto',
          }}>
            {filtered.map(l => (
              <div key={l} onMouseDown={() => addLang(l)} style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F0F7FF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
              >{l}</div>
            ))}
          </div>,
          document.body
        )}
      </div>
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(item => (
            <div key={item.id} style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              background: '#F8FBFF', border: '1px solid #C8D8EC',
              borderRadius: 6, padding: '8px 10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>
                  {item.name}
                </span>
                <DelBtn onClick={() => removeLang(item.id)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', rowGap: 6 }}>
                <select value={item.proficiency || ''}
                  onChange={e => setItems(prev => prev.map(i => i.id === item.id ? { ...i, proficiency: e.target.value } : i))}
                  style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid #C8D8EC', background: 'white', color: 'var(--text)' }}>
                  {PROFICIENCY_LEVELS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <RadioGroup
                  name={`lang-req-${item.id}`}
                  value={item.requirement || ''}
                  onChange={req => setReq(item.id, req)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
          No languages added yet.
        </p>
      )}
    </SectionModal>
  );
};

const EducationModal = ({ initial, saving, onSave, onCancel }) => {
  const [fieldOfStudy, setFieldOfStudy] = useState(initial?.fieldOfStudy || '');
  const [minLevel, setMinLevel]         = useState(initial?.minLevel     || '');
  const [requirement, setRequirement]   = useState(initial?.requirement  || '');

  return (
    <SectionModal
      title={initial ? 'Edit Education' : 'Add Education'}
      saving={saving}
      onSave={() => onSave({ fieldOfStudy, minLevel, requirement })}
      onCancel={onCancel}
      disabled={!fieldOfStudy.trim()}>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Field of Study <span style={{ color: '#CC0000' }}>*</span></label>
        <input value={fieldOfStudy} onChange={e => setFieldOfStudy(e.target.value)}
          style={inputStyle} placeholder="e.g. Accounting, Computer Science…" autoFocus />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Qualification Level</label>
        <select value={minLevel} onChange={e => setMinLevel(e.target.value)} style={inputStyle}>
          {EDUCATION_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Requirement</label>
        <RadioGroup value={requirement} onChange={setRequirement} />
      </div>
    </SectionModal>
  );
};

const ExperienceModal = ({ initial, saving, onSave, onCancel }) => {
  const [positionName, setPositionName] = useState(initial?.positionName || '');
  const [adminLevel, setAdminLevel]     = useState(initial?.adminLevel   || '');
  const [minYears, setMinYears]         = useState(initial?.minYears     || '');
  const [requirement, setRequirement]   = useState(initial?.requirement  || '');

  return (
    <SectionModal
      title={initial ? 'Edit Experience' : 'Add Experience'}
      saving={saving}
      onSave={() => { if (!positionName.trim()) return; onSave({ positionName, adminLevel, minYears, requirement }); }}
      onCancel={onCancel}>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Position Name</label>
        <input value={positionName} onChange={e => setPositionName(e.target.value)}
          style={inputStyle} placeholder="e.g. Project Manager, Senior Accountant…" autoFocus />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Admin Level</label>
        <select value={adminLevel} onChange={e => setAdminLevel(e.target.value)} style={inputStyle}>
          {ADMIN_LEVELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Duration</label>
        <select value={minYears} onChange={e => setMinYears(e.target.value)} style={inputStyle}>
          {EXP_MIN_YEARS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Requirement</label>
        <RadioGroup value={requirement} onChange={setRequirement} />
      </div>
    </SectionModal>
  );
};

const SkillsModal = ({ title, initial, saving, onSave, onCancel }) => {
  const [items, setItems] = useState(() => (initial || []).map(s => ({ ...s })));
  const [input, setInput] = useState('');
  const inputRef          = useRef(null);

  const addSkill = () => {
    const name = input.trim();
    if (!name) return;
    setItems(prev => [...prev, { id: newId(), name, requirement: '' }]);
    setInput('');
    inputRef.current?.focus();
  };

  const removeItem = (id) => setItems(prev => prev.filter(s => s.id !== id));
  const setReq = (id, req) => setItems(prev => prev.map(s => s.id === id ? { ...s, requirement: req } : s));

  return (
    <SectionModal title={title || 'Skills & Knowledge'} saving={saving}
      onSave={() => onSave(items)}
      onCancel={onCancel}>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Add Skill</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
            style={{ ...inputStyle, flex: 1 }}
            placeholder="Type a skill and press Enter…"
            autoFocus
          />
          <AddBtn onClick={addSkill} title="Add" />
        </div>
      </div>
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(s => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#F8FBFF', border: '1px solid #C8D8EC',
              borderRadius: 6, padding: '6px 10px',
            }}>
              <span style={{ flex: 1, fontSize: 13 }}>{s.name}</span>
              <RadioGroup
                name={`skill-req-${s.id}`}
                value={s.requirement || ''}
                onChange={req => setReq(s.id, req)}
              />
              <DelBtn onClick={() => removeItem(s.id)} />
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
          No skills added yet.
        </p>
      )}
    </SectionModal>
  );
};

const AttributesModal = ({ initial, saving, onSave, onCancel }) => {
  const [selected, setSelected] = useState(() => {
    const names = (initial || []).map(a => typeof a === 'string' ? a : a.name);
    return new Set(names);
  });
  const [requirements, setRequirements] = useState(() => {
    const map = {};
    (initial || []).forEach(a => { if (typeof a === 'object' && a.requirement) map[a.name] = a.requirement; });
    return map;
  });

  const toggle = (name) => {
    setSelected(prev => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; });
  };

  return (
    <SectionModal title="Edit Attributes" saving={saving}
      onSave={() => onSave([...selected].map(name => ({ id: newId(), name, requirement: requirements[name] || '' })))}
      onCancel={onCancel}>
      {ATTRIBUTE_SECTIONS.map((section, si) => (
        <div key={si} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#3D80CC', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{section.title}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {section.items.map(item => {
              const checked = selected.has(item);
              return (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(item)}
                      style={{ accentColor: '#3D80CC', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--text)' }}>{item}</span>
                  </label>
                  {checked && (
                    <RadioGroup name={`attr-${item}`} value={requirements[item] || ''}
                      onChange={req => setRequirements(prev => ({ ...prev, [item]: req }))} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </SectionModal>
  );
};

// ── Position card ──────────────────────────────────────────

const ReqLabel = ({ requirement }) => {
  if (!requirement) return null;
  return requirement === 'REQUIRED'
    ? <strong style={{ fontSize: 12 }}> (Required)</strong>
    : <span style={{ fontSize: 12, fontStyle: 'italic', fontWeight: 'normal' }}> (Preferred)</span>;
};

const PositionCard = ({ jobDef, workplaces, snippets, isAdmin, onDelete, onUpdate }) => {
  const { activeOrg, activeProfile } = useAuth();
  const { confirm, dialog }          = useConfirm();
  const [localRoles, setLocalRoles]  = useState(() => Array.isArray(jobDef.roles) ? jobDef.roles : []);
  const [newRoleId, setNewRoleId]    = useState(null);
  const [fieldEdit, setFieldEdit]    = useState(null);
  const [idealEdit, setIdealEdit]    = useState(null);
  const [saving, setSaving]          = useState(false);

  // Expand / collapse state — persisted across sessions
  const [expanded,    setExpanded]    = usePersistedState(`tikhet:joblibrary:${jobDef.id}:expanded`, true);
  const [detailsOpen, setDetailsOpen] = usePersistedState(`tikhet:joblibrary:${jobDef.id}:details`, true);
  const [descOpen,    setDescOpen]    = usePersistedState(`tikhet:joblibrary:${jobDef.id}:desc`, true);
  const [rolesOpen,   setRolesOpen]   = usePersistedState(`tikhet:joblibrary:${jobDef.id}:roles`, true);
  const [idealOpen,   setIdealOpen]   = usePersistedState(`tikhet:joblibrary:${jobDef.id}:ideal`, true);

  const persistRoles = async (updatedRoles) => {
    const { data } = await orgAPI.updateJobDef(activeOrg.id, activeProfile.id, jobDef.id, { roles: updatedRoles });
    setLocalRoles(updatedRoles);
    onUpdate(data.data.jobDef);
  };

  const handleSaveRole = async (roleId, { title, tasks }) => {
    const updatedRoles = localRoles.map((r) => r.id === roleId ? { ...r, title, tasks } : r);
    await persistRoles(updatedRoles);
  };

  const handleDeleteRole = async (roleId) => {
    const role = localRoles.find((r) => r.id === roleId);
    const ok = await confirm('Remove Role', `Remove "${role?.title || 'this role'}"? This cannot be undone.`);
    if (!ok) return;
    await persistRoles(localRoles.filter((r) => r.id !== roleId));
  };

  const handleAddRole = () => {
    const id = newId();
    setNewRoleId(id);
    setLocalRoles((prev) => [...prev, { id, title: '', tasks: [] }]);
    if (!rolesOpen) setRolesOpen(true);
  };

  // ── Field-row editing ──

  const openField = (field) => {
    const val =
      field === 'occupation'  ? (jobDef.name || '') :
      field === 'adminLevel'  ? (jobDef.seniorityLevel || 'UNSPECIFIED') :
      field === 'workplace'   ? (jobDef.workplaceId || '') :
      field === 'description' ? (jobDef.description || '') :
                                (jobDef.schedule || '');
    setFieldEdit({ field, val });
  };
  const closeField = () => { setFieldEdit(null); setSaving(false); };
  const setVal     = (v) => setFieldEdit(fe => ({ ...fe, val: v }));

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await orgAPI.updateJobDef(activeOrg.id, activeProfile.id, jobDef.id, patch);
      onUpdate(data.data.jobDef);
      closeField();
    } finally { setSaving(false); }
  };

  // ── Ideal Candidate editing ──

  const saveIdealSection = async (section, newList) => {
    const currentIdeal = jobDef.idealCandidate || {};
    setSaving(true);
    try {
      const { data } = await orgAPI.updateJobDef(activeOrg.id, activeProfile.id, jobDef.id, {
        idealCandidate: { ...currentIdeal, [section]: newList },
      });
      onUpdate(data.data.jobDef);
      setIdealEdit(null);
    } finally { setSaving(false); }
  };

  // For languages and skills (full-list modals) — unchanged pattern
  const saveIdeal = (section, value) => saveIdealSection(section, value);

  // For education / experience — single-entry add/edit
  const handleSaveIdealEntry = async (section, entryData) => {
    const currentList = Array.isArray(ic[section]) ? ic[section] : [];
    const newList = idealEdit.mode === 'add'
      ? [...currentList, { ...entryData, id: newId() }]
      : currentList.map(e => e.id === idealEdit.entry.id ? { ...entryData, id: e.id } : e);
    await saveIdealSection(section, newList);
  };

  const handleRemoveIdealEntry = async (section, entryId) => {
    const ok = await confirm('Remove Entry', 'Remove this entry? This cannot be undone.');
    if (!ok) return;
    const currentList = Array.isArray(ic[section]) ? ic[section] : [];
    await saveIdealSection(section, currentList.filter(e => e.id !== entryId));
  };

  // ── Pencil buttons ──

  const PBtn = ({ field }) => isAdmin ? (
    <EditBtn onClick={() => openField(field)} />
  ) : null;

  const IPBtn = ({ section }) => isAdmin ? (
    <EditBtn onClick={() => setIdealEdit({ section })} />
  ) : null;

  // ── Display values ──

  const adminLevelDisplay = jobDef.seniorityLevel && jobDef.seniorityLevel !== 'UNSPECIFIED'
    ? ADMIN_LEVEL_LABEL[jobDef.seniorityLevel] : null;
  const workplaceDisplay  = jobDef.workplace?.name || null;
  const scheduleDisplay   = jobDef.schedule ? SCHEDULE_LABEL[jobDef.schedule] : null;

  const ic           = jobDef.idealCandidate || {};
  const icLanguages  = Array.isArray(ic.languages)  ? ic.languages  : [];
  const icEducation  = Array.isArray(ic.education)  ? ic.education  : [];
  const icExperience = Array.isArray(ic.experience) ? ic.experience : [];
  const icSkillsObj  = (ic.skills && !Array.isArray(ic.skills)) ? ic.skills : {};
  const icAttributes = Array.isArray(ic.attributes) ? ic.attributes : [];

  const Chevron = ({ open }) => open
    ? <ChevronUp  size={14} style={{ color: '#4A6FA5', flexShrink: 0 }} />
    : <ChevronDown size={14} style={{ color: '#4A6FA5', flexShrink: 0 }} />;

  return (
    <>
      {dialog}
      <div className="section-box" style={{ marginBottom: 12 }}>

        {/* ── Card header (expand/collapse) ── */}
        <div style={subHeaderStyle}>
          <button
            onClick={() => setExpanded(e => !e)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, flex: 1, padding: 0,
              fontFamily: 'inherit', textAlign: 'left' }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>{jobDef.name}</span>
            {expanded
              ? <ChevronUp  size={14} style={{ opacity: 0.8, flexShrink: 0 }} />
              : <ChevronDown size={14} style={{ opacity: 0.8, flexShrink: 0 }} />}
          </button>
          {isAdmin && (
            <DelBtn onClick={onDelete} title="Delete" />
          )}
        </div>

        {/* ── Expanded card body ── */}
        {expanded && (
          <>
            {/* ── Position Details sub-section ── */}
            <div style={subSectionDivStyle} onClick={() => setDetailsOpen(o => !o)}>
              <span style={subSectionLabelStyle}>Position Details</span>
              <Chevron open={detailsOpen} />
            </div>
            {detailsOpen && (
              <div style={{ padding: '8px 0 4px' }}>
                <div style={fieldRowStyle}>
                  <div style={fieldLabelStyle}>Occupation:</div>
                  <div style={fieldValueStyle}>
                    <span style={{ fontSize: 13 }}>{jobDef.name}</span>
                    <PBtn field="occupation" />
                  </div>
                </div>
                <div style={fieldRowStyle}>
                  <div style={fieldLabelStyle}>Admin Level:</div>
                  <div style={fieldValueStyle}>
                    {adminLevelDisplay
                      ? <span style={{ fontSize: 13 }}>{adminLevelDisplay}</span>
                      : <span style={emptyStyle}>Not set</span>}
                    <PBtn field="adminLevel" />
                  </div>
                </div>
                <div style={fieldRowStyle}>
                  <div style={fieldLabelStyle}>Workplace:</div>
                  <div style={fieldValueStyle}>
                    {workplaceDisplay
                      ? <span style={{ fontSize: 13 }}>{workplaceDisplay}</span>
                      : <span style={emptyStyle}>Not set</span>}
                    <PBtn field="workplace" />
                  </div>
                </div>
                <div style={fieldRowStyle}>
                  <div style={fieldLabelStyle}>Schedule:</div>
                  <div style={fieldValueStyle}>
                    {scheduleDisplay
                      ? <span style={{ fontSize: 13 }}>{scheduleDisplay}</span>
                      : <span style={emptyStyle}>Not set</span>}
                    <PBtn field="schedule" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Position Description sub-section ── */}
            <div style={subSectionDivStyle} onClick={() => setDescOpen(o => !o)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={subSectionLabelStyle}>Position Description</span>
                {isAdmin && (
                  <EditBtn onClick={(e) => { e.stopPropagation(); openField('description'); }} title="Edit Description" />
                )}
              </div>
              <Chevron open={descOpen} />
            </div>
            {descOpen && (
              <div style={{ padding: '10px 16px 14px' }}>
                {jobDef.description
                  ? <div
                      className="rte-editor"
                      style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.55 }}
                      dangerouslySetInnerHTML={{ __html: jobDef.description }}
                    />
                  : <p style={emptyStyle}>
                      No description.{isAdmin && ' Click the pencil to add one — it will sync with Job Ads that use this template.'}
                    </p>
                }
              </div>
            )}

            {/* ── Core Roles & Main Tasks sub-section ── */}
            <div style={subSectionDivStyle} onClick={() => setRolesOpen(o => !o)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={subSectionLabelStyle}>Core Roles & Main Tasks</span>
                {isAdmin && (
                  <AddBtn onClick={(e) => { e.stopPropagation(); handleAddRole(); }} title="Add Role" />
                )}
              </div>
              <Chevron open={rolesOpen} />
            </div>
            {rolesOpen && (
              <div style={{ padding: '10px 16px 14px' }}>
                {localRoles.length === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 4px' }}>
                    No roles defined yet.{isAdmin && ' Use Add Role above to get started.'}
                  </p>
                )}
                {localRoles.map((role, ri) => (
                  <RoleItem
                    key={role.id}
                    role={role}
                    roleIndex={ri}
                    isAdmin={isAdmin}
                    initialEditing={role.id === newRoleId}
                    onSave={handleSaveRole}
                    onDelete={handleDeleteRole}
                  />
                ))}
              </div>
            )}

            {/* ── Ideal Candidate Profile sub-section ── */}
            <div style={subSectionDivStyle} onClick={() => setIdealOpen(o => !o)}>
              <span style={subSectionLabelStyle}>Ideal Candidate Profile</span>
              <Chevron open={idealOpen} />
            </div>
            {idealOpen && (
              <div style={{ padding: '12px 16px 14px' }}>

                {/* Languages */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span className="doc-h2" style={{ color: 'var(--secondary)', marginBottom: 0 }}>Languages</span>
                    <IPBtn section="languages" />
                  </div>
                  {icLanguages.length > 0
                    ? <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                        {icLanguages.map((l, i) => {
                          const name = typeof l === 'string' ? l : l.name;
                          const prof = typeof l === 'string' ? null : l.proficiency;
                          const req  = typeof l === 'string' ? null : l.requirement;
                          return (
                            <li key={l.id || i} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>
                              {name}{prof && PROFICIENCY_LABEL[prof] ? ` — ${PROFICIENCY_LABEL[prof]}` : ''}<ReqLabel requirement={req} />
                            </li>
                          );
                        })}
                      </ul>
                    : <p style={emptyStyle}>Not specified</p>
                  }
                </div>

                {/* Education */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span className="doc-h2" style={{ color: 'var(--secondary)', marginBottom: 0 }}>Education</span>
                    {isAdmin && (
                      <AddBtn onClick={() => setIdealEdit({ section: 'education', mode: 'add' })} title="Add Education" />
                    )}
                  </div>
                  {icEducation.length > 0
                    ? <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                        {icEducation.map(e => (
                          <li key={e.id} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4, lineHeight: 1.6 }}>
                            {e.minLevel ? `${e.minLevel} in ` : ''}{e.fieldOfStudy}
                            {' '}<ReqLabel requirement={e.requirement} />
                            {isAdmin && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 4, verticalAlign: 'middle' }}>
                                <EditBtn onClick={() => setIdealEdit({ section: 'education', mode: 'edit', entry: e })} />
                                <DelBtn onClick={() => handleRemoveIdealEntry('education', e.id)} />
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    : <p style={emptyStyle}>Not specified</p>
                  }
                </div>

                {/* Experience */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span className="doc-h2" style={{ color: 'var(--secondary)', marginBottom: 0 }}>Experience</span>
                    {isAdmin && (
                      <AddBtn onClick={() => setIdealEdit({ section: 'experience', mode: 'add' })} title="Add Experience" />
                    )}
                  </div>
                  {icExperience.length > 0
                    ? <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                        {icExperience.map(e => (
                          <li key={e.id} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4, lineHeight: 1.6 }}>
                            {e.minYears ? (EXP_YEAR_LABEL[e.minYears] || `${e.minYears}+ years`) : ''}
                            {e.positionName ? ` as ${e.positionName}` : ''}
                            {e.adminLevel && ADMIN_LEVEL_LABEL[e.adminLevel] ? ` — ${ADMIN_LEVEL_LABEL[e.adminLevel]}` : ''}
                            {' '}<ReqLabel requirement={e.requirement} />
                            {isAdmin && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 4, verticalAlign: 'middle' }}>
                                <EditBtn onClick={() => setIdealEdit({ section: 'experience', mode: 'edit', entry: e })} />
                                <DelBtn onClick={() => handleRemoveIdealEntry('experience', e.id)} />
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    : <p style={emptyStyle}>Not specified</p>
                  }
                </div>

                {/* Skills & Knowledge */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span className="doc-h2" style={{ color: 'var(--secondary)', marginBottom: 0 }}>Skills & Knowledge</span>
                  </div>
                  {SKILL_SUBCATEGORIES.map(cat => {
                    const items = Array.isArray(icSkillsObj[cat.key]) ? icSkillsObj[cat.key] : [];
                    return (
                      <div key={cat.key} style={{ marginBottom: 10, paddingLeft: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                          <span className="doc-h3" style={{ color: 'var(--text-mid)', marginBottom: 0 }}>{cat.label}</span>
                          {isAdmin && (
                            <EditBtn onClick={() => setIdealEdit({ section: 'skills', subKey: cat.key })} />
                          )}
                        </div>
                        {items.length > 0
                          ? <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                              {items.map(s => (
                                <li key={s.id} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>
                                  {s.name}<ReqLabel requirement={s.requirement} />
                                </li>
                              ))}
                            </ul>
                          : <p style={{ ...emptyStyle, fontSize: 12 }}>Not specified</p>
                        }
                      </div>
                    );
                  })}
                </div>

                {/* Attributes */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    <span className="doc-h2" style={{ color: 'var(--secondary)', marginBottom: 0 }}>Attributes</span>
                    <IPBtn section="attributes" />
                  </div>
                  {icAttributes.length > 0
                    ? <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                        {icAttributes.map((a, i) => {
                          const name = typeof a === 'string' ? a : a.name;
                          const req  = typeof a === 'string' ? null : a.requirement;
                          return (
                            <li key={i} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>
                              {name}<ReqLabel requirement={req} />
                            </li>
                          );
                        })}
                      </ul>
                    : <p style={emptyStyle}>Not specified</p>
                  }
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Per-field modals */}
      {fieldEdit?.field === 'occupation' && (
        <SectionModal title="Edit Occupation" saving={saving}
          onSave={() => saveField({ name: fieldEdit.val })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Occupation <span style={{ color: '#CC0000' }}>*</span></label>
            <input type="text" value={fieldEdit.val}
              onChange={e => setVal(e.target.value)}
              style={inputStyle} autoFocus placeholder="e.g. Senior Accountant" />
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'adminLevel' && (
        <SectionModal title="Edit Admin Level" saving={saving}
          onSave={() => saveField({ seniorityLevel: fieldEdit.val })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Admin / Seniority Level</label>
            <select value={fieldEdit.val} onChange={e => setVal(e.target.value)} style={inputStyle}>
              {ADMIN_LEVELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'workplace' && (
        <SectionModal title="Edit Workplace" saving={saving}
          onSave={() => saveField({ workplaceId: fieldEdit.val || null })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Workplace</label>
            <select value={fieldEdit.val || ''} onChange={e => setVal(e.target.value)} style={inputStyle}>
              <option value="">— Not assigned —</option>
              {(workplaces || []).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'schedule' && (
        <SectionModal title="Edit Schedule" saving={saving}
          onSave={() => saveField({ schedule: fieldEdit.val || null })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Schedule</label>
            <select value={fieldEdit.val || ''} onChange={e => setVal(e.target.value)} style={inputStyle}>
              {SCHEDULES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'description' && (
        <RichTextModal
          title="Edit Position Description"
          initialHtml={fieldEdit.val}
          maxChars={4000}
          snippets={snippets}
          onSave={(html) => saveField({ description: html || null })}
          onCancel={closeField}
        />
      )}

      {/* Ideal Candidate modals */}
      {idealEdit?.section === 'languages' && (
        <IcpLanguagesModal
          initial={icLanguages}
          saving={saving}
          onSave={async (langs) => { await saveIdeal('languages', langs); }}
          onCancel={() => setIdealEdit(null)}
        />
      )}
      {idealEdit?.section === 'education' && (
        <EducationModal
          initial={idealEdit.mode === 'edit' ? idealEdit.entry : null}
          saving={saving}
          onSave={(entryData) => handleSaveIdealEntry('education', entryData)}
          onCancel={() => setIdealEdit(null)}
        />
      )}
      {idealEdit?.section === 'experience' && (
        <ExperienceModal
          initial={idealEdit.mode === 'edit' ? idealEdit.entry : null}
          saving={saving}
          onSave={(entryData) => handleSaveIdealEntry('experience', entryData)}
          onCancel={() => setIdealEdit(null)}
        />
      )}
      {idealEdit?.section === 'skills' && (
        <SkillsModal
          title={SKILL_SUBCATEGORIES.find(c => c.key === idealEdit.subKey)?.label || 'Skills'}
          initial={Array.isArray(icSkillsObj[idealEdit.subKey]) ? icSkillsObj[idealEdit.subKey] : []}
          saving={saving}
          onSave={(items) => saveIdealSection('skills', { ...icSkillsObj, [idealEdit.subKey]: items })}
          onCancel={() => setIdealEdit(null)}
        />
      )}
      {idealEdit?.section === 'attributes' && (
        <AttributesModal
          initial={icAttributes}
          saving={saving}
          onSave={(items) => saveIdealSection('attributes', items)}
          onCancel={() => setIdealEdit(null)}
        />
      )}
    </>
  );
};

// ── Main page ──────────────────────────────────────────────

export const EmployerJobLibrary = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const { confirm, dialog }   = useConfirm();
  const [jobDefs, setJobDefs]       = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [snippets, setSnippets]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null);
  const [parsing, setParsing]       = useState(false);
  const fileInputRef                = useRef(null);

  useEffect(() => {
    if (!activeOrg?.id || !activeProfile?.id) { setLoading(false); return; }
    Promise.all([
      orgAPI.getJobDefs(activeOrg.id, activeProfile.id),
      orgAPI.getWorkplaces(activeOrg.id, activeProfile.id),
      snippetAPI.getSnippets(activeOrg.id, activeProfile.id),
    ])
      .then(([jobDefsRes, workplacesRes, snipRes]) => {
        setJobDefs(jobDefsRes.data.data.jobDefs || []);
        setWorkplaces(workplacesRes.data.data.workplaces || []);
        setSnippets(snipRes.data.data.snippets || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile]);

  const handleSave = async (form) => {
    if (modal?.mode === 'edit') {
      const { data } = await orgAPI.updateJobDef(activeOrg.id, activeProfile.id, modal.jobDef.id, form);
      setJobDefs((prev) => prev.map((j) => j.id === modal.jobDef.id ? data.data.jobDef : j));
    } else {
      const { data } = await orgAPI.createJobDef(activeOrg.id, activeProfile.id, form);
      setJobDefs((prev) => [...prev, data.data.jobDef].sort((a, b) => a.name.localeCompare(b.name)));
    }
    setModal(null);
  };

  const handleDelete = async (jobDef) => {
    const ok = await confirm('Delete Position', `Delete "${jobDef.name}"? This cannot be undone.`);
    if (!ok) return;
    await orgAPI.deleteJobDef(activeOrg.id, activeProfile.id, jobDef.id);
    setJobDefs((prev) => prev.filter((j) => j.id !== jobDef.id));
  };

  const handleUpdate = (updated) =>
    setJobDefs((prev) => prev.map((j) => j.id === updated.id ? updated : j));

  const handleUploadClick = () => {
    if (parsing) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setParsing(true);
    const toastId = toast.loading('Parsing position… this may take up to a minute.');
    try {
      const { data } = await orgAPI.parseJobDef(activeOrg.id, activeProfile.id, file);
      const jobDef = data.data.jobDef;
      toast.success(`Parsed "${jobDef.name}" — review and edit below`, { id: toastId });
      setJobDefs((prev) => [...prev, jobDef].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to parse position', { id: toastId });
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="fade-in">
      <input
        ref={fileInputRef}
        type="file"
        accept={PARSE_ACCEPT}
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>Positions</h1>
          <p>Define the positions within this profile. Each position can be assigned to a job ad.</p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={parsing}
              title="Upload a document describing a position (PDF, Word, ODT, RTF) to parse into a new template"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)',
                color: 'white', borderRadius: 4, padding: '3px 10px',
                cursor: parsing ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'inherit', opacity: parsing ? 0.7 : 1,
              }}
            >
              <Upload size={13} />
              {parsing ? 'Parsing…' : 'Upload Position'}
            </button>
            <AddBtn onClick={() => setModal({ mode: 'add' })} size={14} title="Add Position" />
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : jobDefs.length === 0 ? (
        <div className="section-box">
          <div className="section-box-body" style={{ textAlign: 'center', padding: '48px 20px' }}>
            <Briefcase size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 16px' }}>
              No positions defined yet. Add positions to build your profile's position library.
            </p>
            {isAdmin && (
              <AddBtn onClick={() => setModal({ mode: 'add' })} size={14} title="Add First Position" />
            )}
          </div>
        </div>
      ) : (
        jobDefs.map((jobDef) => (
          <PositionCard
            key={jobDef.id}
            jobDef={jobDef}
            workplaces={workplaces}
            snippets={snippets}
            isAdmin={isAdmin}
            onDelete={() => handleDelete(jobDef)}
            onUpdate={handleUpdate}
          />
        ))
      )}

      {modal && (
        <PositionModal
          initial={modal.mode === 'edit' ? modal.jobDef : null}
          workplaces={workplaces}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {dialog}
    </div>
  );
};
